import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCurrencies } from "@/api/payments";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Edit, Clock, RefreshCw, ArrowUpDown, Plus } from "lucide-react";
import { Currency } from "@/types";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const updateCurrency = async (code: string, exchangeRate: number) => {
  const { error } = await supabase
    .from("currencies")
    .update({
      exchange_rate: exchangeRate,
      last_updated: new Date().toISOString(),
    })
    .eq("code", code);

  if (error) throw error;
  return { code, exchangeRate };
};

// Get color for currency badge (similar to LanguagesList)
const getCurrencyBadgeColor = (code: string): string => {
  const colorPairs = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-500",
    "bg-amber-500/10 text-amber-500",
    "bg-green-500/10 text-green-500",
    "bg-purple-500/10 text-purple-500",
    "bg-rose-500/10 text-rose-500",
    "bg-teal-500/10 text-teal-500",
    "bg-orange-500/10 text-orange-500",
    "bg-indigo-500/10 text-indigo-500",
  ];
  const codeNum = code
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = codeNum % colorPairs.length;
  return colorPairs[index];
};

const CurrenciesList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editCurrency, setEditCurrency] = useState<Currency | null>(null);
  const [newRate, setNewRate] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"code" | "name">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { data: currencies = [], isLoading } = useQuery({
    queryKey: ["currencies"],
    queryFn: fetchCurrencies,
  });

  // Filter and sort currencies
  const filteredCurrencies = currencies.filter(
    (currency) =>
      currency.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currency.code.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const sortedCurrencies = [...filteredCurrencies].sort((a, b) => {
    if (sortBy === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else {
      return sortDirection === "asc"
        ? a.code.localeCompare(b.code)
        : b.code.localeCompare(a.code);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (params: { code: string; exchangeRate: number }) =>
      updateCurrency(params.code, params.exchangeRate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      toast({
        title: "Exchange rate updated",
        description:
          "The currency exchange rate has been updated successfully.",
      });
      setIsDialogOpen(false);
      setEditCurrency(null);
      setNewRate("");
    },
    onError: (error) => {
      toast({
        title: "Error updating exchange rate",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEditClick = (currency: Currency) => {
    setEditCurrency(currency);
    setNewRate(currency.exchangeRate.toString());
    setIsDialogOpen(true);
  };

  const handleUpdateRate = () => {
    if (!editCurrency) return;
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate <= 0) {
      toast({
        title: "Invalid exchange rate",
        description: "Please enter a valid positive number.",
        variant: "destructive",
      });
      return;
    }
    updateMutation.mutateAsync({
      code: editCurrency.code,
      exchangeRate: rate,
    });
  };

  const handleSort = (column: "code" | "name") => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  return (
    <AdminLayout title="Exchange Rates">
      <div className="flex justify-between items-center mb-6">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Search currencies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Edit className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search currencies..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <div className="rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50">
                <TableHead
                  className="w-[120px] cursor-pointer"
                  onClick={() => handleSort("code")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Code</span>
                    {sortBy === "code" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Currency</span>
                    {sortBy === "name" && (
                      <ArrowUpDown
                        className={`h-4 w-4 ${
                          sortDirection === "asc" ? "transform rotate-180" : ""
                        }`}
                      />
                    )}
                  </div>
                </TableHead>
                <TableHead>Symbol</TableHead>
                <TableHead>Exchange Rate (1 USD =)</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : sortedCurrencies.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="h-24 text-center text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Clock className="h-8 w-8 mb-2 text-muted-foreground/50" />
                      <h3 className="font-medium">No currencies found</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery
                          ? `No currencies match "${searchQuery}"`
                          : "There are no currencies defined yet."}
                      </p>
                      {searchQuery && (
                        <Button
                          variant="outline"
                          onClick={() => setSearchQuery("")}
                          className="mt-2"
                          size="sm"
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                sortedCurrencies.map((currency) => (
                  <TableRow key={currency.code} className="hover:bg-muted/50">
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-mono uppercase font-medium px-2 py-1 border-transparent",
                          getCurrencyBadgeColor(currency.code)
                        )}
                      >
                        {currency.code}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {currency.name}
                    </TableCell>
                    <TableCell>{currency.symbol}</TableCell>
                    <TableCell>
                      {currency.code === "USD"
                        ? "1.0 (base)"
                        : currency.exchangeRate.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {currency.lastUpdated ? (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {format(new Date(currency.lastUpdated), "PPp")}
                        </div>
                      ) : (
                        "Not available"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEditClick(currency)}
                        disabled={currency.code === "USD"}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Exchange Rate</DialogTitle>
            <DialogDescription>
              Update the exchange rate for {editCurrency?.name} (
              {editCurrency?.code}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="exchange-rate">
                Exchange Rate (1 USD = ? {editCurrency?.code})
              </Label>
              <Input
                id="exchange-rate"
                type="number"
                step="0.01"
                min="0.01"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="Enter exchange rate"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRate}>Update Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CurrenciesList;
